import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CheckIcon } from "../../design-system/icons";
import { typography, borders, textColors, surfaces } from "../../design-system/tokens";
import { getFontFamilyPreviewStack } from "../../core/utils/fontFamilyUtils";

const VIEWPORT_PADDING = 8;
const DROPDOWN_WIDTH_PX = 220;
const DROPDOWN_MAX_HEIGHT_PX = 240;

const InlineFontFamilyPicker = ({
  value,
  choices = [],
  onChange,
  triggerClassName = "",
  disabled = false,
  title = "Change font family",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  const normalizedValue = useMemo(() => {
    const rawValue = typeof value === "string" ? value.trim() : "";
    return rawValue || "sans";
  }, [value]);

  const normalizedChoices = useMemo(() => {
    const seen = new Set();
    return (Array.isArray(choices) ? choices : [])
      .map((choice) => {
        const choiceValue = String(choice?.value || "").trim();
        if (!choiceValue || seen.has(choiceValue)) return null;
        seen.add(choiceValue);
        return {
          value: choiceValue,
          label: String(choice?.label || choiceValue).trim() || choiceValue,
        };
      })
      .filter(Boolean);
  }, [choices]);

  const dropdownStyle = useMemo(() => {
    if (typeof window === "undefined") {
      return {
        zIndex: 9999,
        top: `${VIEWPORT_PADDING}px`,
        right: `${VIEWPORT_PADDING}px`,
      };
    }

    const rect = triggerRef.current?.getBoundingClientRect?.();
    if (!rect) {
      return {
        zIndex: 9999,
        top: `${VIEWPORT_PADDING}px`,
        right: `${VIEWPORT_PADDING}px`,
      };
    }

    return {
      zIndex: 9999,
      top: `${Math.max(
        VIEWPORT_PADDING,
        Math.min(
          rect.bottom + 8,
          window.innerHeight - DROPDOWN_MAX_HEIGHT_PX - VIEWPORT_PADDING,
        ),
      )}px`,
      right: `${Math.max(VIEWPORT_PADDING, window.innerWidth - rect.right)}px`,
      width: `min(${DROPDOWN_WIDTH_PX}px, calc(100vw - 1rem))`,
      maxWidth: "calc(100vw - 1rem)",
    };
  }, [isOpen]);

  const handleSelect = useCallback(
    (nextValue) => {
      if (!nextValue || disabled) return;
      onChange?.(nextValue);
      setIsOpen(false);
    },
    [disabled, onChange],
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleClick = (event) => {
      if (dropdownRef.current?.contains(event.target)) return;
      if (triggerRef.current?.contains(event.target)) return;
      setIsOpen(false);
    };

    const handleKey = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    // Use capture phase so we still detect outside clicks even when
    // sibling pickers/buttons (e.g. the style preset picker trigger) call
    // event.stopPropagation() in their own handlers. Without capture, React's
    // SyntheticEvent.stopPropagation() also halts the native event before it
    // bubbles to document, leaving this dropdown stuck open.
    document.addEventListener("mousedown", handleClick, true);
    document.addEventListener("touchstart", handleClick, true);
    document.addEventListener("keydown", handleKey);

    return () => {
      document.removeEventListener("mousedown", handleClick, true);
      document.removeEventListener("touchstart", handleClick, true);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isOpen]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        title={title}
        aria-label={title}
        aria-expanded={isOpen}
        disabled={disabled}
        className={triggerClassName}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (disabled) return;
          setIsOpen((prev) => !prev);
        }}
        onPointerDown={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <span
          className={`
            ${typography.caption} ${textColors.brightest}
            relative -top-px inline-flex h-4 min-w-[1.25rem] items-center justify-center
            font-semibold leading-none tracking-tight
          `}
          style={{ fontFamily: getFontFamilyPreviewStack(normalizedValue) }}
          aria-hidden="true"
        >
          Aa
        </span>
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          data-no-dnd="true"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          className={`editor-theme fixed overflow-hidden rounded-lg shadow-2xl ${surfaces.elevated} ${borders.default}`}
          style={dropdownStyle}
        >
          <div
            className="flex flex-col gap-1 overflow-y-auto p-1.5"
            style={{ maxHeight: `${DROPDOWN_MAX_HEIGHT_PX}px` }}
          >
            {normalizedChoices.map((choice) => {
              const isSelected = choice.value === normalizedValue;
              return (
                <button
                  key={choice.value}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleSelect(choice.value);
                  }}
                  className={`
                    flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left transition-colors
                    ${isSelected
                      ? "border-blue-600 bg-blue-50 ring-2 ring-blue-200 dark:border-blue-400 dark:bg-blue-900/20 dark:ring-blue-900/50"
                      : `${borders.default} ${surfaces.primary} ${surfaces.hover}`
                    }
                  `}
                >
                  <span
                    className={`${typography.bodySmall} ${textColors.primary} min-w-0 truncate`}
                    style={{ fontFamily: getFontFamilyPreviewStack(choice.value) }}
                    title={choice.label}
                  >
                    {choice.label}
                  </span>
                  {isSelected ? <CheckIcon size="sm" color="primary" /> : null}
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default InlineFontFamilyPicker;
